import { defineStore } from 'pinia'
import { useCookie } from '@core/composable/useCookie'
import { setLocale } from '@/plugins/i18n'
import { ability } from '@/plugins/casl/ability'
import { railsApi } from '@/utils/railsApi'

// Materio VerticalNav gates items via CASL. Stock kit admin login grants
// manage/all so nav items without action/subject still render.
const TRAINER_ABILITY_RULES = [{ action: 'manage', subject: 'all' }]

function setSignedInFlag(value) {
  useCookie('trainerSignedIn').value = value ? '1' : null
}

function applyTrainerAbilities() {
  useCookie('userAbilityRules').value = TRAINER_ABILITY_RULES
  ability.update(TRAINER_ABILITY_RULES)
}

function clearTrainerAbilities() {
  useCookie('userAbilityRules').value = null
  ability.update([])
}

// The Trainer's chosen `locale` must always visibly affect the UI
// (`design-system.md` §2.6) — sync it into the running i18n instance
// every time the trainer record is loaded or saved.
function syncLocale(trainer) {
  if (trainer?.locale)
    setLocale(trainer.locale)
}

export const useTrainerAccountStore = defineStore('trainerAccount', {
  state: () => ({
    trainer: null,
    loading: false,
    error: null,
    initialized: false,
    deactivationPreview: null,
  }),

  getters: {
    isSignedIn: state => !!state.trainer,
    lifecycleState: state => state.trainer?.lifecycle_state ?? null,
    lifecycleLabel: state => state.trainer?.lifecycle_label ?? '',
    isOnboarding: state => state.trainer?.lifecycle_state === 'onboarding',
    isActive: state => state.trainer?.lifecycle_state === 'active',
    isSuspended: state => state.trainer?.lifecycle_state === 'suspended',
    isDeactivated: state => state.trainer?.lifecycle_state === 'deactivated',
    canUseTrainerFeatures: state => !!state.trainer?.can_use_trainer_features,
  },

  actions: {
    postLoginRedirectPath() {
      if (!this.trainer)
        return '/login'
      if (this.isSuspended || this.isDeactivated)
        return '/account-restricted'
      if (this.isOnboarding)
        return '/onboarding'

      return '/dashboard'
    },

    async bootstrap() {
      if (this.initialized)
        return this.trainer

      this.loading = true
      this.error = null
      try {
        if (!useCookie('trainerSignedIn').value) {
          this.trainer = null
          clearTrainerAbilities()

          return null
        }
        const data = await railsApi('/api/v1/trainer')

        this.trainer = data.trainer
        setSignedInFlag(true)
        applyTrainerAbilities()
        syncLocale(this.trainer)

        return this.trainer
      }
      catch (err) {
        if (err.status === 401) {
          this.trainer = null
          setSignedInFlag(false)
          clearTrainerAbilities()

          return null
        }
        this.error = err.message

        return null
      }
      finally {
        this.loading = false
        this.initialized = true
      }
    },

    async register({ email, password, passwordConfirmation }) {
      this.loading = true
      this.error = null
      try {
        return await railsApi('/api/v1/trainers/registration', {
          method: 'POST',
          body: {
            trainer: {
              email,
              password,
              password_confirmation: passwordConfirmation,
            },
          },
        })
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async login({ email, password }) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/trainers/session', {
          method: 'POST',
          body: { trainer: { email, password } },
        })

        this.trainer = data.trainer
        this.initialized = true
        setSignedInFlag(true)
        applyTrainerAbilities()
        syncLocale(this.trainer)

        return data.trainer
      }
      catch (err) {
        this.error = err.payload?.error || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async logout() {
      this.loading = true
      this.error = null
      try {
        await railsApi('/api/v1/trainers/session', { method: 'DELETE' })
      }
      catch (_) {
        // Idempotent logout — clear local state even if session already gone.
      }
      finally {
        const { clearPwaStateOnLogout } = await import('@/utils/pwaCache')

        await clearPwaStateOnLogout()
        this.trainer = null
        this.initialized = true
        setSignedInFlag(false)
        clearTrainerAbilities()
        this.loading = false
      }
    },

    async confirmEmail(confirmationToken) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi(`/api/v1/trainers/confirmation?confirmation_token=${encodeURIComponent(confirmationToken)}`)

        if (data.trainer) {
          this.trainer = data.trainer
          setSignedInFlag(!!useCookie('trainerSignedIn').value)
          if (useCookie('trainerSignedIn').value)
            applyTrainerAbilities()
          syncLocale(this.trainer)
        }

        return data
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async resendConfirmation(email) {
      return railsApi('/api/v1/trainers/confirmation', {
        method: 'POST',
        body: { trainer: { email } },
      })
    },

    async requestPasswordReset(email) {
      return railsApi('/api/v1/trainers/password', {
        method: 'POST',
        body: { trainer: { email } },
      })
    },

    async resetPassword({ resetPasswordToken, password, passwordConfirmation }) {
      return railsApi('/api/v1/trainers/password', {
        method: 'PUT',
        body: {
          trainer: {
            reset_password_token: resetPasswordToken,
            password,
            password_confirmation: passwordConfirmation,
          },
        },
      })
    },

    async fetchProfile() {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/trainer')

        this.trainer = data.trainer
        setSignedInFlag(true)
        applyTrainerAbilities()
        syncLocale(this.trainer)

        return data.trainer
      }
      catch (err) {
        this.error = err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async updateProfile(profile) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/trainer', {
          method: 'PATCH',
          body: { trainer: profile },
        })

        this.trainer = data.trainer
        syncLocale(this.trainer)
        
        return data.trainer
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async changePassword({ currentPassword, password, passwordConfirmation }) {
      this.loading = true
      this.error = null
      try {
        return await railsApi('/api/v1/trainer_password', {
          method: 'PATCH',
          body: {
            trainer: {
              current_password: currentPassword,
              password,
              password_confirmation: passwordConfirmation,
            },
          },
        })
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async loadDeactivationPreview() {
      this.loading = true
      this.error = null
      try {
        this.deactivationPreview = await railsApi('/api/v1/trainer_deactivation')
        
        return this.deactivationPreview
      }
      catch (err) {
        this.error = err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },

    async deactivateAccount(acknowledgment) {
      this.loading = true
      this.error = null
      try {
        const data = await railsApi('/api/v1/trainer_deactivation', {
          method: 'POST',
          body: { deactivation: { acknowledgment } },
        })

        this.trainer = data.trainer
        setSignedInFlag(false)
        clearTrainerAbilities()

        return data
      }
      catch (err) {
        this.error = err.payload?.errors?.join(' ') || err.message
        throw err
      }
      finally {
        this.loading = false
      }
    },
  },
})
